/**
 * StatusPill — small rounded label with colored bg + colored text.
 * Matches the Penpot "Vacation / Sick Day / Personal" tag pattern, but
 * reusable anywhere a tonal status badge fits. Pass either:
 *   - A `tone` key (red / amber / green / blue / purple / gray) — picks a
 *     matching Tailwind tint pair (bg-X-100 + text-X-700)
 *   - Explicit `bgColor` + `textColor` for full control (any CSS color)
 */

import * as React from "react";

export type PillTone = "red" | "amber" | "green" | "blue" | "purple" | "gray";

const TONE_CLASSES: Record<PillTone, string> = {
  red: "bg-red-100 text-red-700",
  amber: "bg-amber-100 text-amber-800",
  green: "bg-green-100 text-green-700",
  blue: "bg-blue-100 text-blue-700",
  purple: "bg-purple-100 text-purple-700",
  gray: "bg-gray-100 text-gray-700",
};

export interface StatusPillProps {
  label: string;
  /** Tonal preset; ignored when bgColor + textColor are passed. */
  tone?: PillTone;
  /** Explicit override (CSS color string). */
  bgColor?: string;
  textColor?: string;
  className?: string;
}

export function StatusPill({
  label,
  tone = "gray",
  bgColor,
  textColor,
  className = "",
}: StatusPillProps) {
  const useExplicit = bgColor && textColor;
  const classes = useExplicit ? "" : TONE_CLASSES[tone];
  const style: React.CSSProperties = useExplicit
    ? { backgroundColor: bgColor, color: textColor }
    : {};
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-wide whitespace-nowrap ${classes} ${className}`}
      style={style}
    >
      {label}
    </span>
  );
}
